// ═══════════════════════════════════════════════════════════════════════════════
// live-screen-guide.client.js — EOH Live Screen Guide (frontend)
// Load this AFTER eoh-chat.js. It augments the existing EOH Live panel with:
//   • a screenshot PREVIEW of the shared screen inside the panel
//   • red BOX/ARROW annotations drawn over that preview (never on the OS screen)
//   • a CAPTION bar (subtitle) synced to the spoken voice
//   • STEP-BY-STEP guidance: one step at a time with Prev / Next / Done
//
// It talks to the new backend route: POST {API}/live-guide/analyze
// It reuses existing globals from eoh-chat.js: _liveScreen, S, API, liveSpeak,
// stopLiveAudio, detectLanguage, addMsg.
//
// INTEGRATION (two one-line hooks — see bottom of this file for the exact spots):
//   1) In buildLivePopupAsync → buildHTML(), after the panel body is built, call:
//          EOHGuide.mountPanel(d);
//   2) Recommended: give the PiP window more room for the preview:
//          requestWindow({ width: 380, height: 640 })
// That's it — everything else is self-contained here.
// ═══════════════════════════════════════════════════════════════════════════════
(function (global) {
  'use strict';

  var EOHGuide = {
    plan: null,      // { title, steps:[{n,title,say,detail,alert,done_check,annotations}], total }
    idx: 0,          // 0-based current step
    planCtx: '',     // task context string sent to backend on 'next'
    busy: false,
    lastFrame: null, // last captured dataURL (for redraws)
    ui: null         // panel element refs (live in the PiP/popup document)
  };

  // ── Capture ONE frame from the shared stream (same approach as sendLiveQuestion) ──
  // The stream lives in the PARENT window, so build the <video>/<canvas> here.
  async function captureFrame() {
    var ls = global._liveScreen;
    if (!ls || !ls.active || !ls.stream) return null;
    var track = ls.stream.getVideoTracks()[0];
    if (!track || track.readyState !== 'live') return null;
    var video = document.createElement('video');
    video.srcObject = ls.stream; video.muted = true;
    try { await video.play(); } catch (e) { return null; }
    var canvas = document.createElement('canvas');
    var vw = video.videoWidth || 1280, vh = video.videoHeight || 720;
    var scale = Math.min(1, 1280 / Math.max(vw, vh));   // cap longest side to 1280, keep aspect ratio
    canvas.width  = Math.max(1, Math.round(vw * scale));
    canvas.height = Math.max(1, Math.round(vh * scale));
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    video.pause(); video.srcObject = null;
    return canvas.toDataURL('image/jpeg', 0.6);
  }

  // ── Backend call ───────────────────────────────────────────────────────────
  async function analyze(mode, opts) {
    opts = opts || {};
    var frame = await captureFrame();
    if (!frame) return { error: 'no_frame' };
    EOHGuide.lastFrame = frame;
    var S = global.S || {};
    var body = {
      image: frame,
      mode: mode,                                   // 'plan' | 'next'
      question: opts.question || '',
      step: opts.step || 1,
      plan_context: opts.planCtx || EOHGuide.planCtx || '',
      vendor: S.vendor, product: S.product,
      lang: S._chatLang || 'en-US',
      history: (S.history || []).slice(-4),
      mem_session: S._memSession || S._sessionId || null
    };
    try {
      var res = await global.fetch(global.API + '/live-guide/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + S.token },
        body: JSON.stringify(body)
      });
      return await res.json();
    } catch (e) { return { error: e.message || 'request_failed' }; }
  }

  // ── PUBLIC: start a guided task from a question ("guide me through X") ────────
  EOHGuide.startGuide = async function (question) {
    if (EOHGuide.busy) return;
    var ls = global._liveScreen;
    if (!ls || !ls.active) { if (global.addMsg) global.addMsg('assistant', 'Start Live Screen first, then ask me to guide you.', null); return; }
    EOHGuide.busy = true; setStatus('Planning steps…');
    if (question && global.addMsg) global.addMsg('user', question, null);

    var data = await analyze('plan', { question: question });
    EOHGuide.busy = false;
    if (!data || data.error) { setStatus('Ready'); toast(friendlyError(data)); try { if (EOHGuide.onStep) EOHGuide.onStep(); } catch (e) {} return; }
    // CLARIFY-FIRST: search couldn't verify because the request is ambiguous — ask, don't guess.
    if (data.needs_clarify && data.clarify) { renderClarify(data.clarify, question); setStatus('Ready'); return; }
    if (!data.steps || !data.steps.length) {
      // Model returned a flat answer instead of steps — just show/speak it.
      showCaption(data.caption || 'Done.'); speakCaption(data.caption || '');
      setStatus('Ready'); return;
    }
    EOHGuide.plan = { title: data.title || question || 'Task', steps: data.steps, total: data.steps.length };
    EOHGuide.confidence = data.confidence || 'unverified';
    EOHGuide.planCtx = (data.title || question || '').slice(0, 400);
    EOHGuide.idx = 0;
    if (global.addMsg) global.addMsg('assistant', '📋 ' + EOHGuide.plan.title + ' — ' + EOHGuide.plan.total + ' steps. I\'ll guide you one at a time.', null);
    setAskPlaceholder(true);
    renderStep(EOHGuide.plan.steps[0], /*speak*/ true);
    setStatus('Step 1/' + EOHGuide.plan.total);
  };

  // ── PUBLIC: advance to the next step (re-captures + verifies against live screen) ──
  EOHGuide.next = async function () {
    if (EOHGuide.busy || !EOHGuide.plan) return;
    var last = EOHGuide.plan.total - 1;
    if (EOHGuide.idx >= last) { EOHGuide.finish(); return; }
    EOHGuide.busy = true; setStatus('Checking…');
    var nextN = EOHGuide.idx + 2; // 1-based step number we're moving TO

    var data = await analyze('next', { step: nextN, planCtx: EOHGuide.planCtx });
    EOHGuide.busy = false;
    if (!data || data.error) { setStatus('Step ' + (EOHGuide.idx + 1) + '/' + EOHGuide.plan.total); toast(friendlyError(data)); try { if (EOHGuide.onStep) EOHGuide.onStep(); } catch (e) {} return; }

    // Previous step not actually done? Keep the user on it, show the hint.
    if (data.prev_done === false && data.hint) {
      showCaption('⚠ ' + data.hint); speakCaption(data.hint);
      if (data.annotations && data.annotations.length) drawAnnotations(data.annotations);
      setStatus('Step ' + (EOHGuide.idx + 1) + '/' + EOHGuide.plan.total + ' (not done)');
      try { if (EOHGuide.onStep) EOHGuide.onStep(); } catch (e) {}
      return;
    }
    if (data.finished) { EOHGuide.finish(); return; }

    // Advance. Merge the fresh (screen-accurate) annotations/caption into the cached step.
    EOHGuide.confidence = data.confidence || EOHGuide.confidence || 'unverified';
    EOHGuide.idx += 1;
    var cached = EOHGuide.plan.steps[EOHGuide.idx] || {};
    var merged = {
      n: EOHGuide.idx + 1,
      title: data.title || cached.title || ('Step ' + (EOHGuide.idx + 1)),
      say: data.caption || cached.say || '',
      detail: data.detail || cached.detail || '',
      alert: data.alert || cached.alert || '',
      done_check: data.done_check || cached.done_check || '',
      annotations: (data.annotations && data.annotations.length) ? data.annotations : (cached.annotations || [])
    };
    EOHGuide.plan.steps[EOHGuide.idx] = merged;
    renderStep(merged, true);
    setStatus('Step ' + (EOHGuide.idx + 1) + '/' + EOHGuide.plan.total);
  };

  // ── PUBLIC: correct / note / ask about the CURRENT step (no advance) ─────────
  // Returns true if it handled the message (used by the Ask/mic interception).
  EOHGuide.correct = async function (text, lang) {
    if (!EOHGuide.plan || EOHGuide.busy) return false;
    if (lang) { var S0 = global.S || {}; S0._chatLang = lang; S0._voiceLang = lang; }
    if (text && global.addMsg) global.addMsg('user', text, null);
    EOHGuide.busy = true; setStatus('Thinking…');

    var data = await analyze('correct', { question: text, step: EOHGuide.idx + 1, planCtx: EOHGuide.planCtx });
    EOHGuide.busy = false;
    if (!data || data.error) { setStatus('Step ' + (EOHGuide.idx + 1) + '/' + EOHGuide.plan.total); toast(friendlyError(data)); return true; }

    // Merge the refined guidance into the current step (do NOT change idx).
    var cur = EOHGuide.plan.steps[EOHGuide.idx] || {};
    if (data.title)   cur.title = data.title;
    if (data.caption) cur.say = data.caption;
    if (data.detail)  cur.detail = data.detail;
    cur.alert = data.alert || cur.alert || '';
    if (data.annotations && data.annotations.length) cur.annotations = data.annotations;
    EOHGuide.plan.steps[EOHGuide.idx] = cur;

    // Render: if the user asked a question, lead the caption with the answer.
    setTask(); setPreview(EOHGuide.lastFrame); drawAnnotations(cur.annotations || []);
    var cap = (data.answer ? data.answer + '  —  ' : '') + (cur.say || '');
    if (cur.alert) cap += '  •  ⚠ ' + cur.alert;
    showCaption(cap); setStepMeta(cur); toggleStepControls(true);
    speakCaption(data.answer ? (data.answer + '. ' + (cur.say || '')) : (cur.say || ''));
    if (global.addMsg) global.addMsg('assistant', (data.answer ? data.answer + '\n\n' : '') + (cur.say || ''), null);
    setStatus('Step ' + (EOHGuide.idx + 1) + '/' + EOHGuide.plan.total);
    return true;
  };

  // ── PUBLIC: go back one step (review; uses cached caption + last frame) ───────
  EOHGuide.prev = function () {
    if (!EOHGuide.plan || EOHGuide.idx <= 0) return;
    EOHGuide.idx -= 1;
    renderStep(EOHGuide.plan.steps[EOHGuide.idx], true);
    setStatus('Step ' + (EOHGuide.idx + 1) + '/' + EOHGuide.plan.total);
  };

  // ── PUBLIC: finish / clear the guide ─────────────────────────────────────────
  EOHGuide.finish = function () {
    if (global.addMsg && EOHGuide.plan) global.addMsg('assistant', '✅ ' + EOHGuide.plan.title + ' — all steps complete.', null);
    showCaption('✅ All steps complete.'); speakCaption('All done.');
    EOHGuide.plan = null; EOHGuide.idx = 0; EOHGuide.planCtx = '';
    setStatus('Ready'); clearAnnotations(); setTask();
    toggleStepControls(false); setAskPlaceholder(false);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDERING (all inside the PiP/popup document via EOHGuide.ui refs)
  // ═══════════════════════════════════════════════════════════════════════════
  function renderStep(step, speak) {
    if (!step) return;
    setTask();
    setPreview(EOHGuide.lastFrame);
    drawAnnotations(step.annotations || []);
    var cap = step.say || '';
    if (step.alert) cap += (cap ? '  •  ' : '') + '⚠ ' + step.alert;
    _clearClarify();
    showCaption(cap, EOHGuide.confidence);
    setStepMeta(step);
    toggleStepControls(true);
    if (speak) speakCaption(step.say || step.detail || '');
    try { if (EOHGuide.onStep) EOHGuide.onStep(); } catch (e) {}
  }

  function setPreview(dataURL) {
    var ui = EOHGuide.ui; if (!ui || !ui.img) return;
    if (dataURL) { ui.img.src = dataURL; ui.wrap.style.display = 'block'; }
  }

  // Boxes are FRACTIONS (0..1) of the image → position with % so any rendered size works.
  function drawAnnotations(anns) {
    var ui = EOHGuide.ui; if (!ui || !ui.layer) return;
    ui.layer.innerHTML = '';
    (anns || []).forEach(function (a) {
      var d = ui.doc.createElement('div');
      var color = a.color || '#ef4444';
      d.style.cssText = 'position:absolute;box-sizing:border-box;pointer-events:none;'
        + 'left:' + (a.x * 100) + '%;top:' + (a.y * 100) + '%;'
        + 'width:' + (a.w * 100) + '%;height:' + (a.h * 100) + '%;'
        + 'border:2.5px solid ' + color + ';border-radius:6px;'
        + 'box-shadow:0 0 0 2px rgba(0,0,0,.35),0 0 12px ' + color + '99;'
        + 'animation:eohPulse 1.2s ease-in-out infinite';
      if (a.label) {
        var tag = ui.doc.createElement('span');
        tag.textContent = a.label;
        tag.style.cssText = 'position:absolute;left:0;top:-18px;white-space:nowrap;'
          + 'font:600 10px -apple-system,sans-serif;color:#fff;background:' + color + ';'
          + 'padding:1px 6px;border-radius:5px;max-width:180px;overflow:hidden;text-overflow:ellipsis';
        d.appendChild(tag);
      }
      ui.layer.appendChild(d);
    });
  }
  function clearAnnotations() { if (EOHGuide.ui && EOHGuide.ui.layer) EOHGuide.ui.layer.innerHTML = ''; }

  // ── "Did you mean…?" clarify prompt: options that refine the query, then re-search ──
  function renderClarify(clarify, baseQuestion) {
    var ui = EOHGuide.ui; if (!ui || !ui.cap || !ui.doc) return;
    showCaption(clarify.question || 'Which did you mean?', null);
    var doc = ui.doc;
    var host = doc.getElementById('eohg-clarify');
    if (!host) { host = doc.createElement('div'); host.id = 'eohg-clarify'; host.style.cssText = 'margin:6px 0'; if (ui.cap.parentNode) ui.cap.parentNode.insertBefore(host, ui.cap.nextSibling); }
    host.innerHTML = '';
    (clarify.options || []).forEach(function (opt) {
      var b = doc.createElement('button');
      b.textContent = opt;
      b.style.cssText = 'display:block;width:100%;text-align:left;margin:4px 0;padding:8px 10px;border-radius:8px;border:1px solid rgba(90,130,180,.4);background:rgba(30,42,60,.75);color:#e2eefb;font-size:12px;line-height:1.4;cursor:pointer';
      b.onclick = function () { host.innerHTML = ''; EOHGuide.startGuide((baseQuestion || '') + ' — specifically: ' + opt); };
      host.appendChild(b);
    });
  }
  function _clearClarify() { try { var h = EOHGuide.ui && EOHGuide.ui.doc && EOHGuide.ui.doc.getElementById('eohg-clarify'); if (h) h.innerHTML = ''; } catch (e) {} }

  function showCaption(text, conf) {
    var ui = EOHGuide.ui; if (!ui || !ui.cap) return;
    ui.cap.innerHTML = '';
    if (!text) { ui.cap.style.display = 'none'; return; }
    var map = { verified: ['✅ verified', '#12cf92'], partial: ['⚠ verify', '#f5be4f'], unverified: ['⚠ confirm in your system', '#f5be4f'] };
    var m = conf && map[conf];
    if (m) { var b = ui.doc.createElement('span'); b.textContent = m[0] + '  ·  '; b.style.color = m[1]; b.style.fontWeight = '700'; ui.cap.appendChild(b); }
    ui.cap.appendChild(ui.doc.createTextNode(text));
    ui.cap.style.display = 'block';
  }

  function setAskPlaceholder(guiding) {
    try {
      var q = EOHGuide.ui && EOHGuide.ui.doc.getElementById('q');
      if (q) q.placeholder = guiding ? '✏️ Correct me, add a note, or ask…' : 'Ask about your screen...';
    } catch (e) {}
  }

  function setTask() {
    var ui = EOHGuide.ui; if (!ui || !ui.task) return;
    if (EOHGuide.plan && EOHGuide.plan.title) {
      ui.task.textContent = '🎯 ' + EOHGuide.plan.title;
      ui.task.style.display = 'block';
    } else {
      ui.task.style.display = 'none';
    }
  }

  function setStepMeta(step) {
    var ui = EOHGuide.ui; if (!ui || !ui.meta) return;
    var pos = EOHGuide.plan ? ('Step ' + (EOHGuide.idx + 1) + ' / ' + EOHGuide.plan.total) : '';
    ui.meta.textContent = (step && step.title ? step.title : '') + (pos ? '   ·   ' + pos : '');
  }

  function toggleStepControls(on) {
    var ui = EOHGuide.ui; if (!ui || !ui.ctrls) return;
    ui.ctrls.style.display = on ? 'flex' : 'none';
    if (ui.prevBtn) ui.prevBtn.disabled = !EOHGuide.plan || EOHGuide.idx <= 0;
    if (ui.nextBtn && EOHGuide.plan) ui.nextBtn.textContent = (EOHGuide.idx >= EOHGuide.plan.total - 1) ? '✓ Done' : 'Next ▶';
  }

  function setStatus(t) {
    try {
      var doc = EOHGuide.ui && EOHGuide.ui.doc; if (!doc) return;
      var el = doc.getElementById('st'); if (el) el.textContent = t;
      var sp = doc.getElementById('st-spin');
      if (sp) sp.style.display = /(?:…|\.\.\.)\s*$/.test(String(t || '')) ? 'inline-block' : 'none';
    } catch (e) {}
  }

  // ── Speak the caption via the existing TTS path, keep the subtitle up while it plays ──
  function speakCaption(text) {
    if (!text) return;
    if (typeof global.liveSpeak === 'function') {
      global.liveSpeak(text);
      // Keep the caption visible until the audio finishes (poll _liveScreen.speaking).
      var ls = global._liveScreen;
      if (ls) {
        clearInterval(EOHGuide._capPoll);
        var started = Date.now();
        EOHGuide._capPoll = setInterval(function () {
          // hide once speaking flips false (after a grace period) or after a hard cap
          if ((!ls.speaking && Date.now() - started > 1200) || Date.now() - started > 20000) {
            clearInterval(EOHGuide._capPoll);
          }
        }, 400);
      }
    }
  }

  function toast(msg) { if (msg && global.addMsg) global.addMsg('assistant', msg, null); }
  function friendlyError(data) {
    if (!data) return 'Something went wrong. Try again.';
    if (data.error === 'no_frame') return 'Couldn\'t grab the screen — is the share still active?';
    if (String(data.error || '').indexOf('limit') !== -1 || data.reason === 'limit_reached') return '⛔ Live Screen limit reached.';
    if (data.reason === 'plan_expired' || data.error === 'plan_expired') return 'Your plan has expired.';
    return 'EOH couldn\'t analyze the screen. Try again.';
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PANEL MOUNT — inject preview + caption + step controls + "Guide me" into the
  // Live panel document. Call this once from buildHTML(d, ...): EOHGuide.mountPanel(d)
  // ═══════════════════════════════════════════════════════════════════════════
  EOHGuide.mountPanel = function (doc) {
    if (!doc || doc.getElementById('eoh-guide-zone')) return; // idempotent
    var css = doc.createElement('style');
    css.textContent =
      '@keyframes eohPulse{0%,100%{opacity:1}50%{opacity:.45}}'
      + '#eoh-guide-zone{margin:8px 0 6px}'
      + '#eohg-task{display:none;font:800 13px/1.35 -apple-system,sans-serif;color:#fff;background:linear-gradient(135deg,rgba(15,181,116,.22),rgba(10,79,204,.22));border:1px solid rgba(15,181,116,.35);border-radius:9px;padding:8px 11px;margin-bottom:7px;text-align:center}'
      + '#eohg-wrap{position:relative;display:none;border:1px solid rgba(255,255,255,.12);border-radius:10px;overflow:hidden;background:#000;margin-bottom:6px;cursor:zoom-in}'
      + '#eohg-wrap.zoom{overflow:auto;max-height:340px;cursor:zoom-out}'
      + '#eohg-inner{position:relative;width:100%;transform-origin:0 0;transition:transform .12s}'
      + '#eohg-wrap.zoom #eohg-inner{transform:scale(2.2)}'
      + '#eohg-img{display:block;width:100%;height:auto}'
      + '#eohg-layer{position:absolute;inset:0}'
      + '#eohg-zoomhint{position:absolute;right:6px;bottom:6px;z-index:3;font:600 9px -apple-system,sans-serif;color:#fff;background:rgba(0,0,0,.6);padding:2px 6px;border-radius:5px;pointer-events:none}'
      + '#eohg-cap{display:none;font:600 12px/1.45 -apple-system,sans-serif;color:#fff;background:rgba(10,12,20,.92);border:1px solid rgba(255,255,255,.12);border-radius:8px;padding:8px 10px;margin-bottom:6px;text-align:center;min-height:18px}'
      + '#eohg-meta{font:600 10px -apple-system,sans-serif;color:rgba(255,255,255,.55);text-align:center;margin-bottom:6px;min-height:12px}'
      + '#st{color:#7dd3fc !important;opacity:.9}'
      + '#eohg-ctrls{display:none;gap:6px;margin-bottom:6px}'
      + '#eohg-ctrls button{flex:1;height:38px;border-radius:9px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.06);color:#fff;font:700 12px -apple-system,sans-serif;cursor:pointer}'
      + '#eohg-ctrls button:hover:not(:disabled){background:rgba(255,255,255,.14)}'
      + '#eohg-ctrls button:disabled{opacity:.35;cursor:default}'
      + '#eohg-next{background:#ef4444 !important;border-color:#ef4444 !important}'
      + '#eohg-next:hover{background:#dc2626 !important}'
      + '#eohg-guidebtn{width:100%;height:40px;border-radius:10px;border:1px solid rgba(15,181,116,.4);background:rgba(15,181,116,.12);color:#0fb574;font:700 12px -apple-system,sans-serif;cursor:pointer;margin-bottom:2px}'
      + '#eohg-guidebtn:hover{background:rgba(15,181,116,.2)}';
    doc.head.appendChild(css);

    var zone = doc.createElement('div'); zone.id = 'eoh-guide-zone';
    zone.innerHTML =
        '<div id="eohg-task"></div>'
      + '<div id="eohg-wrap"><div id="eohg-inner"><img id="eohg-img" alt="shared screen"/><div id="eohg-layer"></div></div><span id="eohg-zoomhint">🔍 tap to zoom</span></div>'
      + '<div id="eohg-meta"></div>'
      + '<div id="eohg-cap"></div>'
      + '<div id="eohg-ctrls">'
      +   '<button id="eohg-prev">◀ Back</button>'
      +   '<button id="eohg-repeat">↻ Repeat</button>'
      +   '<button id="eohg-next">Next ▶</button>'
      + '</div>'
      + '<button id="eohg-guidebtn">🧭 Guide me step-by-step</button>';

    // Insert the zone right after the "Ask" input row if present, else append.
    var askRow = doc.querySelector('.qr');
    if (askRow && askRow.parentNode) askRow.parentNode.insertBefore(zone, askRow.nextSibling);
    else doc.body.appendChild(zone);

    EOHGuide.ui = {
      doc: doc,
      task:  doc.getElementById('eohg-task'),
      wrap:  doc.getElementById('eohg-wrap'),
      img:   doc.getElementById('eohg-img'),
      layer: doc.getElementById('eohg-layer'),
      cap:   doc.getElementById('eohg-cap'),
      meta:  doc.getElementById('eohg-meta'),
      ctrls: doc.getElementById('eohg-ctrls'),
      prevBtn: doc.getElementById('eohg-prev'),
      nextBtn: doc.getElementById('eohg-next')
    };

    // Redraw boxes if the preview image resizes (PiP window resized)
    EOHGuide.ui.img.addEventListener('load', function () {
      var step = EOHGuide.plan && EOHGuide.plan.steps[EOHGuide.idx];
      if (step) drawAnnotations(step.annotations || []);
    });

    // Tap the preview to zoom in on small buttons (toggle 2.2x, scrollable)
    EOHGuide.ui.wrap.addEventListener('click', function () {
      var on = EOHGuide.ui.wrap.classList.toggle('zoom');
      var hint = doc.getElementById('eohg-zoomhint');
      if (hint) hint.textContent = on ? '🔍 tap to shrink' : '🔍 tap to zoom';
    });

    doc.getElementById('eohg-guidebtn').addEventListener('click', function () {
      // Use whatever is typed in the Ask box as the task; else guide the current screen.
      var q = ''; try { q = (doc.getElementById('q').value || '').trim(); doc.getElementById('q').value = ''; } catch (e) {}
      EOHGuide.startGuide(q);
    });
    EOHGuide.ui.prevBtn.addEventListener('click', function () { EOHGuide.prev(); });
    EOHGuide.ui.nextBtn.addEventListener('click', function () { EOHGuide.next(); });
    doc.getElementById('eohg-repeat').addEventListener('click', function () {
      var step = EOHGuide.plan && EOHGuide.plan.steps[EOHGuide.idx];
      if (step) { showCaption((step.say || '') + (step.alert ? '  •  ⚠ ' + step.alert : '')); speakCaption(step.say || ''); }
    });
  };

  // Reset guide state whenever a session ends (hook from stopLiveScreen if desired).
  EOHGuide.reset = function () {
    EOHGuide.plan = null; EOHGuide.idx = 0; EOHGuide.planCtx = '';
    EOHGuide.lastFrame = null; clearInterval(EOHGuide._capPoll);
    if (EOHGuide.ui) { clearAnnotations(); showCaption(''); toggleStepControls(false); setTask(); setAskPlaceholder(false); if (EOHGuide.ui.wrap) EOHGuide.ui.wrap.style.display = 'none'; }
  };

  // Exposed so the EOH Live Screen understanding layer (eoh-live-screen.js) can grab a
  // frame for the /live-screen/understand and /phases calls, and reuse the guide engine.
  EOHGuide.captureFrame = captureFrame;

  global.EOHGuide = EOHGuide;
})(window);

// ═══════════════════════════════════════════════════════════════════════════════
// INTEGRATION HOOKS (apply these small edits in eoh-chat.js)
// ═══════════════════════════════════════════════════════════════════════════════
//
// 1) MOUNT the guide UI into the panel. In buildLivePopupAsync → buildHTML(d, ...),
//    just before the final wiring at the end of buildHTML, add:
//
//        try { if (window.EOHGuide) EOHGuide.mountPanel(d); } catch (e) {}
//
// 2) BIGGER PiP window so the preview fits. Change:
//        requestWindow({ width: 340, height: 440 })
//    to:
//        requestWindow({ width: 380, height: 640 })
//    (and the fallback window.open pw/ph to 380/640).
//
// 3) CLEAR guide state on session end. In stopLiveScreen(), near the top, add:
//
//        try { if (window.EOHGuide) EOHGuide.reset(); } catch (e) {}
//
// 4) (server) Mount the route in your Express app (e.g. app.js / server.js):
//
//        app.use('/live-guide', require('./routes/live-screen-guide'));
//
// 5) (optional) load this file after eoh-chat.js in page_erp-chat.liquid:
//
//        <script src="{{ 'live-screen-guide.client.js' | asset_url }}" defer></script>
// ═══════════════════════════════════════════════════════════════════════════════
